import type { TFunction } from 'react-i18next';
import {
  PaymentInitiatedBy,
  PendingChargeStatus,
  SourceOfMandate,
  TransactionInitiatedBy,
  TransactionSourceType,
} from '@pflegenavi/shared/api';
import type {
  PaymentData,
  PaymentViewData,
  PaymentViewPendingCharge,
} from './types';
import type { PendingCharge } from '@pflegenavi/frontend/api-nursing-home';

export enum TransactionIconType {
  None = 'none',
  Cancel = 'cancel',
  Refund = 'refund',
  Pending = 'pending',
  Warning = 'warning',
}

/**
 * Development-only warning helper to surface inconsistent combinations
 * without breaking runtime behavior in production builds.
 */
function devWarn(message: string, context?: Record<string, unknown>): void {
  try {
    // Determine environment
    const env =
      typeof process !== 'undefined'
        ? process.env?.['NODE_ENV'] ?? 'development'
        : 'development';

    if (env === 'development') {
      // Fail loudly in development to surface contract violations
      throw new Error(`[payments/formatters] ${message}`);
    }

    if (env === 'test') {
      // In tests, warn (do not throw) to avoid breaking unrelated suites
      // eslint-disable-next-line no-console
      if (typeof console !== 'undefined' && console.warn) {
        // eslint-disable-next-line no-console
        console.warn(`[payments/formatters] ${message}`, context ?? {});
      }
    }
  } catch {
    // no-op: logging should never throw
  }
}

/**
 * Sanity check and normalization for (sourceType, initiatedBy) pairs.
 * Example rule: TransactionSourceType.Receipt always counts as employee-initiated
 * for display purposes (initiator name, etc.). If a different initiator is passed,
 * we coerce to Employee and warn in development.
 */
function normalizeInitiatedByForSource(
  sourceType: TransactionSourceType | undefined,
  initiatedBy: TransactionInitiatedBy
): TransactionInitiatedBy {
  if (
    sourceType === TransactionSourceType.Receipt ||
    sourceType === TransactionSourceType.CancelledReceipt
  ) {
    if (initiatedBy !== TransactionInitiatedBy.Employee) {
      devWarn(
        `Receipt source types imply Employee initiation. Coercing to Employee.`,
        { sourceType, receivedInitiatedBy: initiatedBy }
      );
      return TransactionInitiatedBy.Employee;
    }
  }
  return initiatedBy;
}

/**
 * Check if a pending charge has failed
 */
export function isFailedCharge(
  charge: Pick<PaymentViewPendingCharge, 'pending_charge_status'>
): boolean {
  return (
    charge.pending_charge_status === PendingChargeStatus.failed ||
    charge.pending_charge_status === PendingChargeStatus.frozen
  );
}

/**
 * Format payment type text based on source type and initiator
 * Returns only the base payment type without status or date information
 */
export function formatPaymentType(
  sourceType: TransactionSourceType | undefined,
  initiatedBy: TransactionInitiatedBy,
  t: TFunction,
  options?: {
    receiptType?: string;
    sourceOfMandate?: SourceOfMandate;
  }
): string {
  // Normalize inconsistent (sourceType, initiatedBy) pairs
  const normalizedInitiatedBy = normalizeInitiatedByForSource(
    sourceType,
    initiatedBy
  );
  // Check for special source types first (these override initiator)
  if (sourceType === TransactionSourceType.InitialDeposit) {
    return t('transaction-type.initial-deposit');
  }
  if (sourceType === TransactionSourceType.FinalSettlement) {
    return t('transaction-type.final-settlement');
  }
  if (sourceType === TransactionSourceType.Payout) {
    return t('transaction-type.payout');
  }
  if (sourceType === TransactionSourceType.RecurringItem) {
    return t('transaction-type.recurring-item');
  }

  // Handle receipts (both normal and cancelled have same base type)
  if (
    sourceType === TransactionSourceType.Receipt ||
    sourceType === TransactionSourceType.CancelledReceipt
  ) {
    if (options?.receiptType === 'Barauslage') {
      return t('transaction-type.withdrawal');
    }
    return t('transaction-type.receipt');
  }

  // Handle manual deposits and cancelled manual deposits (Stripe-only,
  // these are cash deposits that don't go through pending charges)
  if (
    sourceType === TransactionSourceType.ManualDeposit ||
    sourceType === TransactionSourceType.CancelledManualDeposit
  ) {
    if (normalizedInitiatedBy === TransactionInitiatedBy.Employee) {
      return t('mobile.family-member.cashDeposit'); // "Bareinzahlung"
    }
    if (normalizedInitiatedBy === TransactionInitiatedBy.FamilyMember) {
      return t('mobile.family-member.manualDirectDebitType'); // "Einmalzahlung"
    }
    // System-initiated manual deposit
    return t('mobile.family-member.manualTopup'); // "Einmalzahlung"
  }

  // Handle direct debits and disputes (same base type).
  // On Stripe, neither DirectDebit nor Dispute is employee-initiated,
  // so the sourceOfMandate check only applies to MangoPay.
  if (
    sourceType === TransactionSourceType.DirectDebit ||
    sourceType === TransactionSourceType.Dispute
  ) {
    if (normalizedInitiatedBy === TransactionInitiatedBy.FamilyMember) {
      return t('mobile.family-member.manualDirectDebitType'); // "Einmalzahlung"
    }
    if (normalizedInitiatedBy === TransactionInitiatedBy.Employee) {
      if (
        options?.sourceOfMandate === SourceOfMandate.NursingHome ||
        options?.sourceOfMandate === SourceOfMandate.LegalEntity
      ) {
        return t('mobile.family-member.cashDeposit'); // "Bareinzahlung"
      }
      return t('mobile.family-member.manualDirectDebitType'); // "Einmalzahlung"
    }
    // System-initiated direct debit
    return t('mobile.family-member.directDebitType'); // "SEPA-Lastschrift"
  }

  // Fallback based on initiator (sourceType is undefined, e.g. pending charges)
  if (normalizedInitiatedBy === TransactionInitiatedBy.FamilyMember) {
    return t('mobile.family-member.manualDirectDebitType');
  }
  if (normalizedInitiatedBy === TransactionInitiatedBy.Employee) {
    // Only show "Einmalzahlung" when sourceOfMandate is explicitly set
    // to a non-institution value. Default to "Bareinzahlung" to preserve
    // Stripe behavior where sourceOfMandate is not available.
    if (
      options?.sourceOfMandate &&
      options.sourceOfMandate !== SourceOfMandate.NursingHome &&
      options.sourceOfMandate !== SourceOfMandate.LegalEntity
    ) {
      return t('mobile.family-member.manualDirectDebitType');
    }
    return t('mobile.family-member.cashDeposit');
  }
  return t('mobile.family-member.directDebitType');
}

/**
 * Format payment status text
 * Can include dates for receipts and cancellations
 */
export function formatPaymentStatus(
  pendingChargeStatus: PendingChargeStatus | undefined,
  sourceType: TransactionSourceType | undefined,
  disputed: boolean | undefined,
  cancelled: boolean | undefined,
  t: TFunction,
  options?: {
    receiptDate?: Date;
    cancellationDate?: Date;
    dateFormatter?: (date: Date) => string;
  }
): string | undefined {
  // Check for cancelled source types first
  if (sourceType === TransactionSourceType.CancelledReceipt) {
    if (options?.cancellationDate && options?.dateFormatter) {
      return t('transaction-status.cancelled-with-date', {
        date: options.dateFormatter(options.cancellationDate),
      });
    }
    return t('transaction-type.refund'); // "Gutschrift"
  }

  if (sourceType === TransactionSourceType.CancelledManualDeposit) {
    if (options?.cancellationDate && options?.dateFormatter) {
      return `${t('transaction-status.dated', {
        date: options.dateFormatter(options.cancellationDate),
      })} - ${t('transaction-type.reversed')}`;
    }
    return t('transaction-type.reversed'); // "Rückabgewickelt"
  }

  // Check for dispute source type
  if (sourceType === TransactionSourceType.Dispute) {
    return t('transaction-status.disputed');
  }

  // Check pending charge statuses
  if (pendingChargeStatus) {
    const status = pendingChargeStatus;
    if (
      status === PendingChargeStatus.failed ||
      status === PendingChargeStatus.frozen
    ) {
      return t('transaction-status.failed');
    }
    if (status === PendingChargeStatus.upcoming) {
      return t('transaction-status.upcoming');
    }
    if (status === PendingChargeStatus.pending) {
      return t('transaction-status.pending');
    }
    // Fallback for unknown pending states
    return t('mobile.family-member.pending');
  }

  // Check boolean flags
  if (disputed) {
    return t('transaction-status.disputed');
  }

  if (cancelled) {
    if (options?.cancellationDate && options?.dateFormatter) {
      return t('transaction-status.cancelled-with-date', {
        date: options.dateFormatter(options.cancellationDate),
      });
    }
    return t('transaction-status.cancelled');
  }

  // For regular receipts with dates (not cancelled)
  if (
    sourceType === TransactionSourceType.Receipt &&
    options?.receiptDate &&
    options?.dateFormatter
  ) {
    return t('transaction-status.dated', {
      date: options.dateFormatter(options.receiptDate),
    });
  }

  return undefined;
}

/**
 * Get transaction icon type based on status and type
 */
export function getTransactionIconType(
  pendingChargeStatus: PendingChargeStatus | undefined,
  disputed: boolean | undefined,
  cancelled: boolean | undefined,
  sourceType: TransactionSourceType | undefined
): TransactionIconType {
  // Check for cancelled source types first
  if (
    sourceType === TransactionSourceType.CancelledReceipt ||
    sourceType === TransactionSourceType.CancelledManualDeposit
  ) {
    return TransactionIconType.Refund;
  }

  // Check for dispute source type
  if (sourceType === TransactionSourceType.Dispute) {
    return TransactionIconType.Cancel;
  }

  // Check pending charge statuses
  if (pendingChargeStatus) {
    const status = pendingChargeStatus;
    if (
      status === PendingChargeStatus.failed ||
      status === PendingChargeStatus.frozen
    ) {
      return TransactionIconType.Cancel;
    }
    if (status === PendingChargeStatus.upcoming) {
      return TransactionIconType.Pending;
    }
    if (status === PendingChargeStatus.pending) {
      return TransactionIconType.Warning;
    }
    // Fallback for unknown pending states
    return TransactionIconType.Warning;
  }

  // Check boolean flags
  if (disputed || cancelled) {
    return TransactionIconType.Cancel;
  }

  return TransactionIconType.None;
}

/**
 * Get payment color based on status and type
 */
export function getPaymentColor(
  pendingChargeStatus: string | undefined,
  disputed: boolean | undefined,
  cancelled: boolean | undefined,
  sourceType: TransactionSourceType | undefined,
  amountInCent: number
): 'warning' | 'error' | 'info' | 'success' {
  if (pendingChargeStatus) {
    // Handle different pending charge statuses
    const status = pendingChargeStatus;
    if (
      status === PendingChargeStatus.failed ||
      status === PendingChargeStatus.frozen
    ) {
      return 'error'; // Red for failed/frozen charges
    }
    if (status === PendingChargeStatus.upcoming) {
      return 'info'; // Blue for upcoming/scheduled charges
    }
    if (status === PendingChargeStatus.pending) {
      return 'warning'; // Yellow for pending/processing charges
    }
    // Fallback to warning for unknown pending states
    return 'warning';
  }

  if (disputed || cancelled) {
    return 'error';
  }

  if (
    sourceType === TransactionSourceType.Dispute ||
    sourceType === TransactionSourceType.CancelledManualDeposit
  ) {
    return 'info';
  }

  if (
    sourceType === TransactionSourceType.FinalSettlement ||
    sourceType === TransactionSourceType.Payout
  ) {
    return amountInCent < 0 ? 'error' : 'success';
  }

  if (sourceType === TransactionSourceType.InitialDeposit) {
    return amountInCent < 0 ? 'error' : 'success';
  }

  return 'success';
}

function mapPaymentInitiatedByToTransactionInitiatedBy(
  paymentInitiation: PaymentInitiatedBy
): TransactionInitiatedBy {
  if (paymentInitiation === PaymentInitiatedBy.Disabled) {
    /** this cannot happen in Phoenix anymore */
    return TransactionInitiatedBy.Employee;
  }
  if (paymentInitiation === PaymentInitiatedBy.Employee) {
    return TransactionInitiatedBy.Employee;
  }
  if (paymentInitiation === PaymentInitiatedBy.FamilyMember) {
    return TransactionInitiatedBy.FamilyMember;
  }
  if (paymentInitiation === PaymentInitiatedBy.System) {
    return TransactionInitiatedBy.System;
  }

  return TransactionInitiatedBy.Employee;
}

/**
 * Get the initiator name from a pending charge based on who initiated it.
 *
 * @example
 * const name = getPendingChargeInitiatorName(
 *   charge,
 *   t('web-components.initiator.employee'),
 *   t('web-components.initiator.family-member'),
 *   t('web-components.initiator.system')
 * );
 */
export function getPendingChargeInitiatorName(
  charge: {
    payment_initiation: PaymentInitiatedBy;
    employee_first_name?: string | null;
    employee_last_name?: string | null;
    family_member_first_name?: string | null;
    family_member_last_name?: string | null;
  },
  fallbackEmployee: string,
  fallbackFamilyMember: string,
  fallbackSystem: string
): string {
  if (charge.payment_initiation === PaymentInitiatedBy.Employee) {
    if (charge.employee_first_name && charge.employee_last_name) {
      return `${charge.employee_first_name} ${charge.employee_last_name}`;
    }
    return fallbackEmployee;
  }
  if (charge.payment_initiation === PaymentInitiatedBy.FamilyMember) {
    if (charge.family_member_first_name && charge.family_member_last_name) {
      return `${charge.family_member_first_name} ${charge.family_member_last_name}`;
    }
    return fallbackFamilyMember;
  }
  return fallbackSystem;
}

export function getPaymentViewData(
  payment: Pick<
    PaymentData,
    | 'sourceType'
    | 'initiatedBy'
    | 'amountInCent'
    | 'disputed'
    | 'cancelled'
    | 'creatorName'
    | 'receiptType'
    | 'receiptDate'
    | 'cancelledSubmissionDate'
  >,
  nursingHomeName: string,
  t: TFunction,
  options?: {
    dateFormatter?: (date: Date) => string;
  }
): PaymentViewData {
  const typeText = formatPaymentType(
    payment.sourceType,
    payment.initiatedBy,
    t,
    { receiptType: payment.receiptType }
  );

  const statusText = formatPaymentStatus(
    undefined,
    payment.sourceType,
    payment.disputed,
    payment.cancelled,
    t,
    {
      receiptDate: payment.receiptDate,
      cancellationDate: payment.cancelledSubmissionDate,
      dateFormatter: options?.dateFormatter,
    }
  );

  const color = getPaymentColor(
    undefined,
    payment.disputed,
    payment.cancelled,
    payment.sourceType,
    payment.amountInCent
  );

  const iconType = getTransactionIconType(
    undefined,
    payment.disputed,
    payment.cancelled,
    payment.sourceType
  );

  const initiatorName =
    payment.initiatedBy === TransactionInitiatedBy.Employee ||
    payment.initiatedBy === TransactionInitiatedBy.System
      ? nursingHomeName
      : payment.creatorName ?? '--';

  return {
    typeText,
    statusText,
    color,
    iconType,
    initiatorName,
  };
}

/**
 * Transform payment data to view format
 */
export function getPendingChargeViewData(
  pending_charge: Pick<
    PendingCharge,
    | 'pending_charge_status'
    | 'payment_initiation'
    | 'source_of_mandate'
    | 'family_member_first_name'
    | 'family_member_last_name'
    | 'employee_first_name'
    | 'employee_last_name'
  >,
  nursingHomeName: string,
  t: TFunction
): PaymentViewData {
  const transactionInitiatedBy = mapPaymentInitiatedByToTransactionInitiatedBy(
    pending_charge.payment_initiation
  );
  const typeText = formatPaymentType(undefined, transactionInitiatedBy, t, {
    sourceOfMandate: pending_charge.source_of_mandate,
  });
  const statusText = formatPaymentStatus(
    pending_charge.pending_charge_status,
    undefined,
    false,
    false,
    t
  );
  const color = getPaymentColor(
    pending_charge.pending_charge_status,
    false,
    false,
    undefined,
    0
  );

  const iconType = getTransactionIconType(
    pending_charge.pending_charge_status,
    false,
    false,
    undefined
  );

  const initiatorName = getPendingChargeInitiatorName(
    pending_charge,
    nursingHomeName,
    '--',
    nursingHomeName
  );

  return {
    typeText,
    statusText,
    color,
    iconType,
    initiatorName,
  };
}
